import { useEffect, useState } from "react";
import { ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, istDateTime } from "@/lib/api";
import { RecoveryCodes } from "@/components/LoginPage";

const STATUS = { ok: ["Intact", "up"], tampered: ["TAMPERED", "down"], broken_seal: ["SEAL BROKEN", "down"] };

const Seals = ({ owner }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/security/seals").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const seal = async () => {
    setBusy(true);
    try { await api.post("/security/seal"); toast.success("Today's audit log sealed"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return null;
  const sealedToday = d.seals.some((s) => s.date === d.today);
  return (
    <div className="mt-4" data-testid="audit-seals">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-slate-200">Daily audit seals</span>
        <span className="text-[11px] text-slate-500">Each day's orders, decisions and logins are hash-chained at 15:45 IST and the seal is emailed to you.</span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={load} className="h-7 border-slate-700 text-xs" data-testid="button-verify-seals">Verify now</Button>
          {owner && !sealedToday && <Button size="sm" disabled={busy} onClick={seal} className="h-7 text-xs" data-testid="button-seal-today">Seal today now</Button>}
        </div>
      </div>
      <div className="mt-2 overflow-x-auto">
        <table className="dense w-full text-xs">
          <thead><tr><th className="text-left">Day</th><th className="text-left">Status</th><th className="text-left">Records</th><th className="text-left">Seal hash</th><th className="text-left">Details</th></tr></thead>
          <tbody>
            {d.seals.map((s) => {
              const [label, cls] = STATUS[s.status] || [s.status, ""];
              const bad = [...s.changed.map((x) => `edited ${x}`), ...s.deleted.map((x) => `deleted ${x}`), ...s.backdated.map((x) => `back-dated ${x}`)];
              return (
                <tr key={s.date} data-testid={`seal-row-${s.date}`}>
                  <td className="num">{s.date}</td>
                  <td className={`font-semibold ${cls}`} data-testid={`seal-status-${s.date}`}>{label}</td>
                  <td className="num">{s.count}{s.late ? <span className="text-slate-500"> +{s.late} later</span> : null}</td>
                  <td className="num text-slate-400" title={s.seal_hash}>{s.seal_hash.slice(0, 16)}…</td>
                  <td className="text-[11px] text-red-400">{bad.slice(0, 3).join(" · ")}{bad.length > 3 ? ` +${bad.length - 3} more` : ""}</td>
                </tr>
              );
            })}
            {!d.seals.length && <tr><td colSpan={5} className="text-center text-slate-500" data-testid="seals-empty">No days sealed yet. The first seal is made at 15:45 IST on the next trading day.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Password = () => {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const save = async () => {
    try { await api.post("/auth/password", { current: cur, new: nw }); setCur(""); setNw(""); toast.success("Password changed; your other sessions were signed out"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 flex flex-wrap items-end gap-2" data-testid="change-password">
      <span className="w-full text-xs font-semibold text-slate-200">Change password</span>
      <Input data-testid="input-current-password" type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} className="h-8 w-48 bg-slate-900 text-xs" />
      <Input data-testid="input-new-password" type="password" placeholder="New password (10+ chars)" value={nw} onChange={(e) => setNw(e.target.value)} className="h-8 w-56 bg-slate-900 text-xs" />
      <Button size="sm" disabled={!cur || nw.length < 10} onClick={save} className="h-8 text-xs" data-testid="button-change-password">Change</Button>
    </div>
  );
};

const Sessions = () => {
  const [d, setD] = useState(null);
  const load = () => api.get("/auth/sessions").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const revoke = async (id) => { try { await api.delete(`/auth/sessions/${id}`); load(); toast.success("Session signed out"); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return null;
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2">
      <div data-testid="active-sessions">
        <span className="text-xs font-semibold text-slate-200">Active sessions</span>
        <div className="max-h-56 overflow-y-auto">
        {d.sessions.map((s) => (
          <div key={s.id} className="flex items-center gap-2 border-t border-slate-800 py-1.5 text-[11px]" data-testid="session-row">
            <span className="truncate text-slate-300">{s.email}</span><span className="truncate whitespace-nowrap text-slate-500">{s.role} · {s.ip} · {istDateTime(s.created_at)}</span>
            {s.current ? <span className="ml-auto text-emerald-400">this device</span>
              : <Button size="sm" variant="ghost" onClick={() => revoke(s.id)} className="ml-auto h-6 text-[11px] text-red-400" data-testid={`button-revoke-${s.id}`}>Sign out</Button>}
          </div>
        ))}
        </div>
      </div>
      <div data-testid="login-history">
        <span className="text-xs font-semibold text-slate-200">Login history</span>
        <div className="max-h-56 overflow-y-auto">
          {d.log.map((l) => (
            <div key={l.id} className="flex gap-2 border-t border-slate-800 py-1 text-[11px]" data-testid="login-log-row">
              <span className="num whitespace-nowrap text-slate-500">{istDateTime(l.ts)}</span>
              <span className={`whitespace-nowrap ${l.ok ? "text-slate-300" : "text-red-400"}`}>{l.event.replace(/_/g, " ")}</span>
              <span className="truncate text-slate-500">{l.email} · {l.ip}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Recovery = ({ me, reload }) => {
  const [email, setEmail] = useState(me.recovery_email || "");
  const [otp, setOtp] = useState("");
  const [codes, setCodes] = useState(null);
  const saveEmail = async () => { try { await api.put("/auth/recovery/email", { email }); toast.success("Recovery email saved"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const regen = async () => { try { setCodes((await api.post("/auth/recovery/codes", { otp })).data.recovery_codes); setOtp(""); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2" data-testid="recovery-settings">
      <div className="flex flex-wrap items-end gap-2">
        <span className="w-full text-xs font-semibold text-slate-200">Recovery email <span className="font-normal text-slate-500">(where password-reset codes go; until set, they go to your login email)</span></span>
        <Input data-testid="input-recovery-email" type="email" placeholder="you@gmail.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-8 w-64 bg-slate-900 text-xs" />
        <Button size="sm" disabled={!email || email === me.recovery_email} onClick={saveEmail} className="h-8 text-xs" data-testid="button-save-recovery-email">Save</Button>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <span className="w-full text-xs font-semibold text-slate-200">Recovery codes <span className="font-normal text-slate-500" data-testid="recovery-left">· {me.recovery_left} of 8 left</span></span>
        {codes ? <div className="w-full max-w-sm"><RecoveryCodes codes={codes} onOk={() => setCodes(null)} /></div> : (
          <>
            <Input data-testid="input-regen-otp" placeholder="Authenticator code" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} className="h-8 w-40 bg-slate-900 text-xs" />
            <Button size="sm" variant="outline" disabled={otp.length !== 6} onClick={regen} className="h-8 border-slate-700 text-xs" data-testid="button-regen-codes">Get 8 new codes</Button>
          </>
        )}
      </div>
    </div>
  );
};

const ViewerReset = () => {
  const [pw, setPw] = useState("");
  const [two, setTwo] = useState(true);
  const go = async () => {
    try { await api.post("/auth/users/viewer/reset", { new: pw, reset_2fa: two }); setPw(""); toast.success(`Viewer password reset${two ? " and 2FA cleared (they'll scan a new QR)" : ""}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 flex flex-wrap items-end gap-2" data-testid="viewer-reset">
      <span className="w-full text-xs font-semibold text-slate-200">Reset the viewer account</span>
      <Input data-testid="input-viewer-new-password" type="password" placeholder="New viewer password (10+ chars)" value={pw} onChange={(e) => setPw(e.target.value)} className="h-8 w-60 bg-slate-900 text-xs" />
      <label className="flex items-center gap-1.5 text-[11px] text-slate-300"><input type="checkbox" checked={two} onChange={(e) => setTwo(e.target.checked)} data-testid="checkbox-viewer-reset-2fa" /> also reset their 2FA</label>
      <Button size="sm" disabled={pw.length < 10} onClick={go} className="h-8 text-xs" data-testid="button-viewer-reset">Reset viewer</Button>
    </div>
  );
};

export const SecurityPanel = () => {
  const [me, setMe] = useState(null);
  const load = () => api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!me) return null;
  const owner = me.role === "owner";
  return (
    <div className="panel col-span-12 min-w-0 p-4" data-testid="panel-security">
      <div className="eyebrow flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> Security</div>
      <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-500"><Lock size={11} /> Signed in as {me.email} ({owner ? "owner" : "read-only viewer"}) with 2-step code · session ends {istDateTime(me.expires_at)}</p>
      {owner && <Sessions />}
      <Password />
      <Recovery me={me} reload={load} />
      {owner && <ViewerReset />}
      <Seals owner={owner} />
    </div>
  );
};
